import React, { useMemo, useEffect, useState } from 'react';
import { Heart, ArrowRight, AlertTriangle } from 'lucide-react';
import { supabase } from '../../../Supabase/SupabaseClient';
import './MisFavoritos.css';
import Footer from "../../../Components/Footer/Footer";

const MisFavoritos = () => {
  const [favoritos, setFavoritos] = useState([]);
  const [imagenesInfo, setImagenesInfo] = useState([]);
  const [estilosInfo, setEstilosInfo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Carga usuario + datos
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const { data: { user }, error: authErr } = await supabase.auth.getUser();
        if (authErr) throw authErr;
        if (!user) throw new Error('Debes iniciar sesión para ver tus favoritos');

        // Traer favoritos del usuario
        const { data: favs, error: favErr } = await supabase
          .from('favoritos_usuario')
          .select('id, imagen_id, created_at')
          .eq('usuario_id', user.id)
          .order('created_at', { ascending: false });
        if (favErr) throw favErr;
        setFavoritos(favs || []);

        const imgIds = favs.map(f => f.imagen_id);
        if (imgIds.length > 0) {
          // Traer metadatos de imágenes
          const { data: imgsData, error: imgsErr } = await supabase
            .from('imagenes_inspiracion')
            .select('id, creditos, estilo_id, url_publica')
            .in('id', imgIds);
          if (imgsErr) throw imgsErr;
          setImagenesInfo(imgsData || []);

          // Traer estilos
          const { data: estilosData, error: estilosErr } = await supabase
            .from('estilos_fotograficos')
            .select('id, nombre');
          if (estilosErr) throw estilosErr;
          setEstilosInfo(estilosData || []);
        }
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Función para desmarcar favorito en DB
  const handleToggleFavorito = async (imagenId) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuario no autenticado');

      // Borrar fila en favoritos_usuario
      const { error: delErr } = await supabase
        .from('favoritos_usuario')
        .delete()
        .eq('usuario_id', user.id)
        .eq('imagen_id', imagenId);
      if (delErr) throw delErr;

      // Actualizar estado local
      setFavoritos(prev => prev.filter(f => f.imagen_id !== imagenId));
    } catch (err) {
      console.error('Error al eliminar favorito:', err);
    }
  };

  const favoritosConDetalles = useMemo(() => {
    return favoritos.map(fav => {
      const img = imagenesInfo.find(i => i.id === fav.imagen_id) || {};
      const estilo = estilosInfo.find(e => e.id === img.estilo_id) || {};
      return {
        favId: fav.id,
        imagenId: fav.imagen_id,
        createdAt: fav.created_at,
        creditos: img.creditos,
        estiloNombre: estilo.nombre,
        url: img.url_publica
      };
    });
  }, [favoritos, imagenesInfo, estilosInfo]);

  if (loading) {
    return (
      <div className="fav-container">
        <div className="fav-card fav-card--narrow">
          <p className="fav-loading">Cargando favoritos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fav-container">
        <div className="fav-card fav-card--narrow fav-card--error" role="alert">
          <AlertTriangle size={40} className="fav-card-icon" aria-hidden="true" />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!favoritosConDetalles.length) {
    return (
      <div className="fav-container">
        <div className="fav-card fav-card--narrow">
          <div className="fav-card-icon" aria-hidden="true">
            <Heart size={64} strokeWidth={1.25} />
          </div>
          <h2>No tienes imágenes favoritas aún</h2>
          <p>Explora nuestra galería de inspiración y guarda tus imágenes favoritas para verlas después.</p>
          <a href="/inspiracion" className="fav-btn">
            <span>Ir a Inspiración</span>
            <ArrowRight size={18} aria-hidden="true" />
          </a>
        </div>
      </div>
    );
  }

  return (
    <>
    <section className="fav-container">
      <h2 className="fav-title">Mis Favoritos</h2>

      <div className="fav-grid">
        {favoritosConDetalles.map(item => (
          <article key={item.favId} className="fav-card-item">
            <div className="fav-image-container">
              <img
                src={item.url}
                alt={item.creditos || `Favorito ${item.imagenId}`}
                className="fav-image"
                loading="lazy"
              />
              <button
                type="button"
                onClick={() => handleToggleFavorito(item.imagenId)}
                className="fav-remove"
                aria-label={`Quitar ${item.creditos || 'imagen'} de favoritos`}
              >
                <Heart size={20} fill="#ff4757" aria-hidden="true" />
              </button>
            </div>
            <div className="fav-info">
              <h3>{item.creditos}</h3>
              {item.estiloNombre && <p>{item.estiloNombre}</p>}
            </div>
          </article>
        ))}
      </div>
    </section>
      <Footer />
    </>
  );
};

export default MisFavoritos;